import DataGrid, { Column } from "devextreme-react/data-grid";
import CustomStore from "devextreme/data/data_source";
import React, {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { TriggerContext } from "../../context/TriggerProvider";
import { ValueContext } from "../../context/ValueProvider";
import { useToast } from "../../hooks/toast";
import api from "../../services/api";
import PhotoCell from "./CellRender/PhotoCell";
import { Container } from "./styles";
import { useAuth } from "../../hooks/auth";
import pdsGroups from "../../config/pdsGroups";

export default function EvaluationByProjectsList({
  type,
  isSidePanelFullScreen,
  idEvaluation,
  idAreaType,
  evaluation,
}) {
  const { master } = useContext(ValueContext);
  const isPartnerEvaluationByProject = type === "partner";
  const { addToast } = useToast();
  const dataGridRef = useRef(null);
  const { triggersFunction } = useContext(TriggerContext);
  const [isNewRow, setIsNewRow] = useState(null);
  const { hasScope, user } = useAuth();

  const isAdmin = useMemo(() => hasScope([pdsGroups.Admin]), [hasScope]);

  const evaluationByProjectsDataSource = useMemo(() => {
    return new CustomStore({
      load: async () => {
        try {
          const { data } = await api.get(
            `api/evaluations/${idEvaluation}/senior-projects`
          );
          return data.filter(
            (item) => item.isPartner === isPartnerEvaluationByProject
          );
        } catch (err) {
          addToast({
            type: "error",
            title: "Evaluations couldn't be loaded. Try again later",
          });
          throw err;
        }
      },

      insert: async (values) => {
        try {
          await api.post(`api/evaluations/${idEvaluation}/senior-projects`, {
            ...values,
            isPartner: isPartnerEvaluationByProject,
          });
          triggersFunction("updateGrade");
        } catch (err) {
          addToast({
            type: "error",
            title: "Error inserting evaluation",
          });
          throw err;
        }
      },
      update: async (key, values) => {
        try {
          await api.put(
            `api/evaluations/${idEvaluation}/senior-projects/${key.id}`,
            {
              ...values,
              isPartner: isPartnerEvaluationByProject,
            }
          );
          triggersFunction("updateGrade");
        } catch (err) {
          addToast({
            type: "error",
            title: "Error updating evaluation",
          });
          throw err;
        }
      },

      reshapeOnPush: true,
      loadMode: "raw",
      cacheRawData: true,
    });
  }, [addToast, idEvaluation, isPartnerEvaluationByProject, triggersFunction]);

  const renderPhotoCell = useCallback(
    (e) => {
      const matchedProfessional = master?.professionals?.find(
        (professional) => professional.ID === e.data.idEvaluator
      );

      return <PhotoCell data={matchedProfessional} />;
    },
    [master]
  );

  const canEdit =
    hasScope([pdsGroups.Admin, pdsGroups.Partner]) ||
    evaluation?.idPartner === user.userId;

  // Função de validação personalizada para campos de texto
  const validateTextFields = (e) => {
    if (!isNewRow) {
      // Combina os dados antigos com os novos dados
      const rowData = Object.assign({}, e.oldData, e.newData);

      const fieldText = rowData.fieldText || "";
      const fieldText2 = rowData.fieldText2 || "";
      const fieldText3 = rowData.fieldText3 || "";

      if (!fieldText.trim() || !fieldText2.trim() || !fieldText3.trim()) {
        e.isValid = false;
        e.errorText = "Fill all the text boxes to save this input";
      }
    }
  };

  return (
    <Container
      style={{ width: !isSidePanelFullScreen ? "560px" : "100%", height: 450 }}
    >
      <style>
        {`
          .dx-popup-content .dx-layout-manager .dx-box-flex {
            display: flex !important;
          }
          
          /* Força o grupo de campos de texto a usar layout horizontal */
          .equal-width-row .dx-layout-manager {
            width: 100% !important;
          }
          
          .equal-width-row .dx-layout-manager .dx-box {
            display: flex !important;
            flex-direction: row !important;
            width: 100% !important;
            gap: 15px !important;
          }
          
          .equal-width-row .dx-box-item {
            flex: 1 !important;
            width: calc(33.333% - 10px) !important;
            min-width: 0 !important;
          }
          
          /* Força os TextAreas a ocuparem toda largura de seus containers */
          .equal-width-row .dx-field {
            width: 100% !important;
          }
          
          .equal-width-row .dx-texteditor,
          .equal-width-row .dx-textarea {
            width: 100% !important;
          }
          
          .equal-width-row .dx-texteditor-input,
          .equal-width-row .dx-textarea-content {
            width: 100% !important;
            min-width: 0 !important;
          }
          
          /* Força o popup a não limitar a largura dos campos */
          .dx-popup-content {
            overflow: visible !important;
          }
          
          .dx-popup-content .dx-scrollview-content {
            overflow: visible !important;
          }
          
          /* Remove qualquer max-width que possa estar limitando */
          .equal-width-row * {
            max-width: none !important;
          }
          
          /* Força o form inteiro a usar toda largura */
          .dx-form {
            width: 100% !important;
          }
          
          .dx-form .dx-layout-manager {
            width: 100% !important;
          }
        `}
      </style>
      <DataGrid
        ref={dataGridRef}
        dataSource={evaluationByProjectsDataSource}
        height="100%"
        width="100%"
        onRowUpdating={(options) => {
          options.newData = {
            ...options.oldData,
            ...options.newData,
          };
        }}
        onRowClick={(e) => {
          if (e.rowType === "data") {
            dataGridRef.current.instance.editRow(e.rowIndex);
          }
        }}
        onEditingStart={() => {
          setIsNewRow(false);
        }}
        onInitNewRow={() => {
          setIsNewRow(true);
        }}
        onRowValidating={validateTextFields}
        headerFilter={{ visible: true, allowSearch: true }}
        editing={{
          mode: "popup",
          allowAdding: isAdmin,
          form: {
            colCount: 3,
            items: [
              {
                dataField: "idEvaluator",
                isRequired: true,
                editorOptions: {
                  readOnly: !canEdit,
                },
              },
              {
                dataField: "workDays",
                isRequired: true,
                editorOptions: {
                  readOnly: !canEdit,
                },
              },
              {
                dataField: "idProject",
                isRequired: true,
                colSpan: 3,
                editorOptions: {
                  readOnly: !canEdit,
                },
              },
              {
                dataField: "isActive",
                colSpan: 3,
                editorOptions: {
                  readOnly: !canEdit,
                },
              },
              ...(isNewRow
                ? []
                : [
                    ...(isPartnerEvaluationByProject
                      ? idAreaType !== 10 && idAreaType !== 11
                        ? [
                            {
                              itemType: "group",
                              colCount: 2,
                              colSpan: 3,
                              items: [
                                {
                                  dataField: "fieldValue",
                                  editorOptions: {
                                    readOnly: !canEdit,
                                  },
                                },
                                {
                                  dataField: "fieldValue2",
                                  editorOptions: {
                                    readOnly: !canEdit,
                                  },
                                },
                              ],
                            },
                          ]
                        : [
                            {
                              dataField: "fieldValue",
                              colSpan: 3,
                              editorOptions: {
                                readOnly: !canEdit,
                              },
                            },
                          ]
                      : [
                          {
                            itemType: "group",
                            colCount: 2,
                            colSpan: 3,
                            items: [
                              {
                                dataField: "isApplicable",
                                editorOptions: {
                                  readOnly: !canEdit,
                                },
                                label: {
                                  text: "Do you feel comfortable to evaluate this professional?",
                                },
                              },
                            ],
                          },
                        ]),
                    // Campos de texto igualmente distribuídos
                    {
                      itemType: "group",
                      colCount: 3,
                      colSpan: 3,
                      cssClass: "equal-width-row",
                      alignItemLabels: false,
                      items: [
                        {
                          dataField: "fieldText",
                          editorType: "dxTextArea",
                          colSpan: 1,
                          isRequired: true,
                          caption:
                            idAreaType === 10 || idAreaType === 11
                              ? "Strong Points Key Message"
                              : "Building Relationship Key Message",
                          editorOptions: {
                            height: 200,
                            readOnly: !canEdit,
                          },
                          validationRules: [
                            {
                              type: "required",
                              message: "This field is required",
                            },
                          ],
                        },
                        {
                          dataField: "fieldText2",
                          editorType: "dxTextArea",
                          colSpan: 1,
                          isRequired: true,
                          caption:
                            idAreaType === 10 || idAreaType === 11
                              ? "Development Points Key Message"
                              : "Project Key Message",
                          editorOptions: {
                            height: 200,
                            readOnly: !canEdit,
                          },
                          validationRules: [
                            {
                              type: "required",
                              message: "This field is required",
                            },
                          ],
                        },
                        {
                          dataField: "fieldText3",
                          editorType: "dxTextArea",
                          colSpan: 1,
                          isRequired: true,
                          caption: "Environment & Identity Key Message",
                          editorOptions: {
                            height: 200,
                            readOnly: !canEdit,
                          },
                          validationRules: [
                            {
                              type: "required",
                              message: "This field is required",
                            },
                          ],
                        },
                      ],
                    },
                  ]),
            ],
          },
        }}
      >
        <Column
          dataField="idEvaluator"
          caption="Evaluator"
          alignment="center"
          cellRender={renderPhotoCell}
          lookup={{
            dataSource: master.professionals,
            displayExpr: "Name",
            valueExpr: "ID",
          }}
        />
        <Column
          dataField="workDays"
          dataType="number"
          caption="Work Days"
          alignment="center"
        />
        <Column
          dataField="idProject"
          caption="Project"
          dataType="number"
          cellRender={(e) => (
            <>
              {e.data.projectCode} - {e.data.projectName}
            </>
          )}
          alignment="left"
          lookup={{
            dataSource: master.projects,
            displayExpr: "displayName",
            valueExpr: "ID",
          }}
        />
        <Column
          dataField="isActive"
          caption="Considered?"
          dataType="boolean"
          alignment="center"
        />
        {idAreaType !== 10 && idAreaType !== 11 && (
          <Column
            dataField="fieldValue2"
            caption="Project Grade"
            dataType="number"
            alignment="center"
            visible={isPartnerEvaluationByProject}
            disabled={
              !(
                hasScope([pdsGroups.Admin, pdsGroups.Partner]) ||
                evaluation?.idPartner === user.userId
              )
            }
            lookup={{
              dataSource: [
                { value: -1.0, text: "N/A" },
                { value: 0.0, text: "0.0" },
                { value: 0.25, text: "0.25" },
                { value: 0.5, text: "0.5" },
                { value: 0.75, text: "0.75" },
                { value: 1.0, text: "1.0" },
                { value: 1.25, text: "1.25" },
                { value: 1.5, text: "1.5" },
                { value: 1.75, text: "1.75" },
                { value: 2.0, text: "2.0" },
                { value: 2.25, text: "2.25" },
                { value: 2.5, text: "2.5" },
                { value: 2.75, text: "2.75" },
                { value: 3.0, text: "3.0" },
              ],
              valueExpr: "value",
              displayExpr: "text",
            }}
            editorOptions={{
              dropDownOptions: {
                position: {
                  my: "left top",
                  at: "left bottom",
                  collision: "none",
                },
              },
            }}
          />
        )}
        <Column
          dataField="fieldValue"
          caption={
            idAreaType === 10 || idAreaType === 11
              ? "Overall Grade"
              : "Building Relationship Grade"
          }
          alignment="center"
          visible={isPartnerEvaluationByProject}
          lookup={
            idAreaType === 10 || idAreaType === 11
              ? undefined
              : {
                  dataSource: [
                    { value: -1.0, text: "N/A" },
                    { value: 0.0, text: "0.0" },
                    { value: 0.25, text: "0.25" },
                    { value: 0.5, text: "0.5" },
                    { value: 0.75, text: "0.75" },
                    { value: 1.0, text: "1.0" },
                    { value: 1.25, text: "1.25" },
                    { value: 1.5, text: "1.5" },
                    { value: 1.75, text: "1.75" },
                    { value: 2.0, text: "2.0" },
                    { value: 2.25, text: "2.25" },
                    { value: 2.5, text: "2.5" },
                    { value: 2.75, text: "2.75" },
                    { value: 3.0, text: "3.0" },
                  ],
                  valueExpr: "value",
                  displayExpr: "text",
                }
          }
          editorOptions={{
            dropDownOptions: {
              position: {
                my: "left top",
                at: "left bottom",
                collision: "none",
              },
            },
          }}
        />
        <Column
          dataField="fieldText"
          caption={
            idAreaType === 10 || idAreaType === 11
              ? "Strong Points Key Message"
              : !isPartnerEvaluationByProject
              ? "Did the professional contribute to your professional development in this project? Please, give examples of positive contributions."
              : "Building Relationship Key Message"
          }
          alignment="center"
          visible={false}
        />
        <Column
          dataField="fieldText3"
          caption="Has the professional provided and sustained the environment and has the professional represented Integration's identity in the project? Please, give examples of positive contributions."
          alignment="center"
          visible={false}
        />
        <Column
          dataField="fieldText2"
          caption={
            idAreaType === 10 || idAreaType === 11
              ? "Development Points Key Message"
              : !isPartnerEvaluationByProject
              ? "What are the development points of this professional related to Professional Development, Environment and Identity in the project? Please explain."
              : "Project Key Message"
          }
          alignment="center"
          visible={false}
        />
        <Column
          dataField="isApplicable"
          caption="Applicable?"
          dataType="boolean"
          alignment="center"
          visible={!isPartnerEvaluationByProject}
        />
      </DataGrid>
    </Container>
  );
}